import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { d203Api, FD130 } from '../../services/d203Api';
import { useD203Mode } from './hooks/useD203Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useD203Record } from './hooks/useD203Record';
import { useD203Navigation } from './hooks/useD203Navigation';
import { useD203Permissions } from './hooks/useD203Permissions';
import { useD203Approval } from './hooks/useD203Approval';
import PermissionGuard from '../../components/PermissionGuard';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2ProductDesc from './components/Tab2ProductDesc';
import Tab3Composition from './components/Tab3Composition';
import Tab4Detail from './components/Tab4Detail';
import { SharedActionBar } from '../../components/SharedActionBar';
import { BatchAcceptanceBridge, pwd203f1AcceptanceManifest } from '../../features/acceptance';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { getStatusBadge, isFieldDisabledFn, isLockedForEdit } from './helpers';
import './D203Form.css';

const D203Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useD203Permissions();
  const { mode, setMode, isEditing } = useD203Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, productDesc, compositions, accessories, inspections,
    selectedRow, loading,
    setSelectedRow, setDetails, setCompositions,
    loadRecord, setField, getField,
    setPDField, getPDField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addCompositionRow, deleteCompositionRow,
    revertToRecord, resetForAdd, applyResult, importSCInspections,
    getFieldError, clearFieldErrors,
  } = useD203Record();

  useEffect(() => {
    if (mode === 'add' && formData.fd130_10 && formData.fd130_101) {
      void importSCInspections(String(formData.fd130_10), Number(formData.fd130_101));
    }
  }, [mode, formData.fd130_10, formData.fd130_101, importSCInspections]);

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data, result.pd);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useD203Navigation(loadRecordWithNames);

  // Approval hooks
  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useD203Approval({ record, isEditing, loadRecordWithNames });

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load D203 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FD130) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fd130_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除託工單 ${record.fd130_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Computed
  const statusBadge = getStatusBadge(record || formData);
  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  return (
    <PermissionGuard module={'pwd203f1'} action="query">
    <div className="d203-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">受訂託工單</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>受訂託工單 Subcontract Order</h1>
            <span className="badge badge-default">D203</span>
            {statusBadge && (
              <span
                className={`badge field-label ${statusBadge.cls}`}
                data-contract-field="fd130_80"
              >
                核准狀態：{statusBadge.text}
              </span>
            )}
          </div>
          <div className="page-title-right" />
        </div>

        {/* Form Info Bar */}
        <div className="form-info-bar">
          <div className="field-group">
            <label>託工單號</label>
            <input type="text" readOnly style={{ width: 140 }} value={formData.fd130_01 || ''} data-contract-field="fd130_01" />
          </div>
          <div className="field-group">
            <label>加工廠</label>
            <span style={{ fontSize: 13 }} data-contract-field="fd130_12">{names.processor || '--'}</span>
          </div>
          <div className="field-group">
            <label>S/C NO</label>
            <span style={{ fontSize: 13 }} data-contract-field="fd130_10">{formData.fd130_10 || '--'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '產品說明', '原料/品質/備註', '色號明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData} names={names} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
              resolveSingleName={resolveSingleName} getFieldError={getFieldError}
            />
          </div>

          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2ProductDesc
              productDesc={productDesc} names={names} isEditing={isEditing}
              getPDField={getPDField} setPDField={setPDField}
              resolveSingleName={resolveSingleName} isFieldDisabled={isFieldDisabled}
            />
          </div>

          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Composition
              formData={formData} productDesc={productDesc}
              compositions={compositions} isEditing={isEditing}
              isFieldDisabled={isFieldDisabled} getField={getField} setField={setField}
              getPDField={getPDField} setPDField={setPDField}
              resolveSingleName={resolveSingleName} names={names}
              setCompositions={setCompositions}
              addCompositionRow={addCompositionRow} deleteCompositionRow={deleteCompositionRow}
            />
          </div>

          <div className={`tab-pane ${activeTab === 3 ? 'active' : ''}`}>
            <Tab4Detail
              formData={formData} details={details} selectedRow={selectedRow}
              isEditing={isEditing} setSelectedRow={setSelectedRow}
              getDetailField={getDetailField} setDetailField={setDetailField}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
              getField={getField} names={names} resolveSingleName={resolveSingleName}
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={!!record}
          currentIdx={currentIdx} recordCount={allIds.length}
          onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
          onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
          onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
          onSave={handleAddAction} onCancel={handleModifyAction}
          approval={{
            statusCode: (record?.fd130_80 || '').trim(),
            isApproved: (record?.fd130_80 || '').trim() === 'P',
            isPending: ['D', 'S'].includes((record?.fd130_80 || '').trim()), // 統一碼 D + 舊碼 S 過渡容忍（批次 5）
            isLocked: isLockedForEdit(record),
          }}
          onSubmit={handleSubmit} onApprove={handleApprove}
          onReject={handleReject} onWriteoff={handleWriteoff} onHistory={handleViewHistory}
          onExit={() => navigate('/erp')}
          canCreate={perms.canCreate} canEdit={perms.canEdit}
          canDelete={perms.canDelete} canApprove={perms.canApprove}
          acceptanceActionIds={{
            list: 'PWD203F1-ACT-LIST', get: 'PWD203F1-ACT-GET',
            create: 'PWD203F1-ACT-CREATE', update: 'PWD203F1-ACT-UPDATE',
            delete: 'PWD203F1-ACT-DELETE', submit: 'PWD203F1-ACT-SUBMIT',
            approve: 'PWD203F1-ACT-APPROVE', reject: 'PWD203F1-ACT-REJECT',
            writeoff: 'PWD203F1-ACT-WRITEOFF',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwd203f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fd130_01 ? [record.fd130_01] : []}
          keyOf={(item) => item.fd130_01}
          queryExact={(key) => d203Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && handleNavTo(0)}
          autoFillDisabled={allIds.length === 0}
          autoFillReason="載入正式 API 已存在的第一筆託工單作範例，不直接寫業務資料。"
          screenDraft={{ master: formData, details, product_description: productDesc, compositions, accessories, inspections }}
        />
      </div>

      {/* Approval History Modal */}
      {record && (
        <ApprovalHistoryModal
          visible={approvalHistoryVisible}
          orderNo={record.fd130_01}
          onClose={() => setApprovalHistoryVisible(false)}
        />
      )}
    </div>
    </PermissionGuard>
  );
};

export default D203Form;
